import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  Dispatch,
  SetStateAction
} from 'react'
import { socket } from "../index"
import { useAuth } from './auth'
import { useTheme } from './theme'
import makeToast from '../components/toast'

interface EmbossersContext {
  embossers: Embosser[],
  embossersCount: number,
  search: string,
  availableManufacturers: string[],
  availableFirmwareVersions: string[],
  availableDistributors: string[],
  availableCountryCodes: string[],
  availableArchitectures: string[],
  availableModels: string[],
  pagesCount: number,
  currentPage: number,
  filtersList: {
    label: string,
    value: string,
    setFilterDispatch: Dispatch<SetStateAction<string>>
  }[],
  sortBySerialNumber: "default" | "ascending" | "descending",
  sortByConnectionDate: "default" | "ascending" | "descending",
  filterByManufacturer: string,
  filterByFirmwareVersion: string,
  filterByCompany: string,
  filterByCountry: string,
  filterByArchitecture: string,
  filterByModel: string,
  resultsListLength: number,
  itemsOnPage: number,
  displayMode: string,
  setSearch: Dispatch<SetStateAction<string>>,
  setCurrentPage: Dispatch<SetStateAction<number>>,
  setDisplayMode: Dispatch<SetStateAction<string>>,
  setItemsOnPage: Dispatch<SetStateAction<number>>,
  setSortBySerialNumber: Dispatch<SetStateAction<"default" | "ascending" | "descending">>,
  setSortByConnectionDate: Dispatch<SetStateAction<"default" | "ascending" | "descending">>,
  setFilterByManufacturer: Dispatch<SetStateAction<string>>,
  setFilterByFirmwareVersion: Dispatch<SetStateAction<string>>,
  setFilterByCompany: Dispatch<SetStateAction<string>>,
  setFilterByCountry: Dispatch<SetStateAction<string>>,
  setFilterByArchitecture: Dispatch<SetStateAction<string>>,
  setFilterByModel: Dispatch<SetStateAction<string>>,
  error: string
}

export interface Embosser {
  id: string
  firmware: {
    timestamp: string
    lastupdate: string
    version: string
  }
  connected: {
    date: string
    hour: string
    timeRemaining: string
  }
  type: string
  hostname: string
  hammerbatch: string
  hammerBatchOptions: string
  protocol: string
  distributor: {
    address: string
    countrycode: string
    email: string
    category: string
    phone: string
    name: string
    id: string
  }
  delivered: string
  ports: {
    ssh: string
    ws: string
  }
  arch: string
  mac: string
  macAddresses: {
    bluetooth: string
    ethernet: string
    wifi: string
  }
  model: string
  fid: string
  tested: {
    by: string
    date: string
  }
  serial: string
  counter: string
  manufactured: string
}

const Embossers = createContext({} as EmbossersContext)

export default function EmbossersProvider(
  { children }: { children: ReactNode }
) {
  const [embossers, setEmbossers] = useState<Embosser[]>([])
  const [embossersCount, setEmbossersCount] = useState<number>(0)
  const [error, setError] = useState<string>('')
  const [search, setSearch] = useState<string>('')
  const {companies, serialNumbers, user, manufacturer, embossersNotifications, currentRole} = useAuth()
  const [availableManufacturers, setAvailableManufacturers] = useState<string[]>([])
  const [availableFirmwareVersions, setAvailableFirmwareVersions] = useState<string[]>([])
  const [availableDistributors, setAvailableDistributors] = useState<string[]>([])
  const [availableCountryCodes, setAvailableCountryCodes] = useState<string[]>([])
  const [availableArchitectures, setAvailableArchitectures] = useState<string[]>([])
  const [availableModels, setAvailableModels] = useState<string[]>([])
  const [pagesCount, setPagesCount] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [resultsListLength, setResultsListLength] = useState<number>(0)
  const [itemsOnPage, setItemsOnPage] = useState<number>(15)
  const [sortBySerialNumber, setSortBySerialNumber] = useState<"default" | "ascending" | "descending">("default")
  const [sortByConnectionDate, setSortByConnectionDate] = useState<"default" | "ascending" | "descending">("default")
  const [filterByManufacturer, setFilterByManufacturer] = useState<string>("")
  const [filterByFirmwareVersion, setFilterByFirmwareVersion] = useState<string>("")
  const [filterByCompany, setFilterByCompany] = useState<string>("")
  const [filterByCountry, setFilterByCountry] = useState<string>("")
  const [filterByArchitecture, setFilterByArchitecture] = useState<string>("")
  const [filterByModel, setFilterByModel] = useState<string>("")
  const [displayMode, setDisplayMode] = useState<string>(() => {
    const savedDisplayMode = localStorage.getItem("embossersDisplayMode");
    return JSON.parse(savedDisplayMode!) || "list";
  })
  const {mode} = useTheme()
  const filtersList: {
    label: string,
    value: string,
    setFilterDispatch: Dispatch<SetStateAction<string>>
  }[] = [
    {
      label: "Manufacturer",
      value: filterByManufacturer,
      setFilterDispatch: setFilterByManufacturer
    },
    {
      label: "Firmware Version",
      value: filterByFirmwareVersion,
      setFilterDispatch: setFilterByFirmwareVersion
    },
    {
      label: "Company",
      value: filterByCompany,
      setFilterDispatch: setFilterByCompany
    },
    {
      label: "Country",
      value: filterByCountry,
      setFilterDispatch: setFilterByCountry
    },
    {
      label: "Architecture",
      value: filterByArchitecture,
      setFilterDispatch: setFilterByArchitecture
    },
    {
      label: "Model",
      value: filterByModel,
      setFilterDispatch: setFilterByModel
    }
  ]

  const updateEmbossers = (
    admin: boolean,
    companies: string,
    search: string,
    serialNumbers: string,
    manufacturer: string,
    role: string,
    currentPage: number,
    itemsOnPage: number,
    sortBySerialNumber: string,
    sortByConnectionDate: string,
    filterByManufacturer: string,
    filterByFirmwareVersion: string,
    filterByCompany: string,
    filterByCountry: string,
    filterByArchitecture: string,
    filterByModel: string
  ) => {
    if (role !== undefined && role !== 'None') {
      socket.emit('get_embossers', admin, companies, search, serialNumbers, manufacturer, currentPage, itemsOnPage, sortBySerialNumber.toLowerCase(), sortByConnectionDate.toLowerCase(), filterByManufacturer.toLowerCase(), filterByFirmwareVersion.toLowerCase(), filterByCompany.toLowerCase(), filterByCountry.toLowerCase(), filterByArchitecture.toLowerCase(), filterByModel.toLowerCase())
    }
  }

  useEffect(() => {
    const embossersReceived = function (data: any) {
      if (data.error) {
        if (currentRole !== undefined && currentRole !== "None" && currentRole !== "Blocked" && user && embossersNotifications === "on") {
          makeToast('error', 'An error has occured.', mode)
        }
        setError(data.error)
      } else {
        if (data.embossersPagesCount < currentPage) {
          setCurrentPage(1)
        }
        setEmbossers(data.embossers || [])
        setEmbossersCount(data.embossersCount)
        setPagesCount(data.embossersPagesCount)
        setResultsListLength(data.searchResults)
        setAvailableManufacturers(data.availableManufacturers?.sort())
        setAvailableFirmwareVersions(data.availableFirmwareVersions?.sort())
        setAvailableDistributors(data.availableDistributors?.sort())
        setAvailableCountryCodes(data.availableCountryCodes?.sort())
        setAvailableArchitectures(data.availableArchitectures?.sort())
        setAvailableModels(data.availableModels?.sort())
      }
    }
    const embosserConnected = function (data: any) {
      if (!data.error) {
        if (currentRole.includes("Administrator") || (currentRole !== undefined && currentRole !== "None" && currentRole !== "Blocked" && user && embossersNotifications === 'on' && (user.companies === '' || user.companies.split(';').includes(data.companies)) && (user.manufacturer === '' || user?.manufacturer.toLowerCase().split(';').includes(data.manufacturer)) && (user.serialNumbers === '' || user?.serialNumbers.split(',').includes(data.serial)) && !(user.companies === '' && user.serialNumbers === ''))) {
          makeToast('success', `Embosser connected\nSerial: ${data.serial}`, mode)
        }
      }
    }
    const embosserDisconnected = function (data: any) {
      if (!data.error) {
        if (currentRole.includes("Administrator") || (currentRole !== undefined && currentRole !== "None" && currentRole !== "Blocked" && user && embossersNotifications === 'on' && (user.companies === '' || user.companies.split(';').includes(data.companies)) && (user.manufacturer === '' || user?.manufacturer.toLowerCase().split(';').includes(data.manufacturer)) && (user.serialNumbers === '' || user?.serialNumbers.split(',').includes(data.serial)) && !(user.companies === '' && user.serialNumbers === ''))) {
          makeToast('success', `Embosser disconnected\nSerial: ${data.serial}`, mode)
        }
      }
    }
    socket.on('embossers_received', embossersReceived)
    socket.on('embosser_connected', embosserConnected)
    socket.on('embosser_disconnected', embosserDisconnected)
    return () => {
        socket.off('embossers_received', embossersReceived)
        socket.off('embosser_connected', embosserConnected)
        socket.off('embosser_disconnected', embosserDisconnected)
    }
    // eslint-disable-next-line
  }, [mode, currentRole, companies, serialNumbers, manufacturer, embossersNotifications, currentPage, itemsOnPage])

  useEffect(() => {
    for (let filtersIterator of filtersList) {
      filtersIterator.setFilterDispatch("")
    }
    // eslint-disable-next-line
  }, [user?.companies, user?.manufacturer, user?.serialNumbers])

  useEffect(() => {
    updateEmbossers(currentRole.includes('Administrator'), companies, search, serialNumbers, manufacturer, currentRole, currentPage, itemsOnPage, sortBySerialNumber, sortByConnectionDate, filterByManufacturer, filterByFirmwareVersion, filterByCompany, filterByCountry, filterByArchitecture, filterByModel)
    // eslint-disable-next-line
  }, [search, currentRole, companies, serialNumbers, manufacturer, currentPage, itemsOnPage, sortBySerialNumber, sortByConnectionDate, filterByManufacturer, filterByFirmwareVersion, filterByCompany, filterByCountry, filterByArchitecture, filterByModel])

  useEffect(() => {
    socket.on('embossers_list_modified', () => {
      updateEmbossers(currentRole.includes('Administrator'), companies, search, serialNumbers, manufacturer, currentRole, currentPage, itemsOnPage, sortBySerialNumber, sortByConnectionDate, filterByManufacturer, filterByFirmwareVersion, filterByCompany, filterByCountry, filterByArchitecture, filterByModel)
    })
    return () => {
      socket.off('embossers_list_modified')
    }
  }, [search, currentRole, companies, serialNumbers, manufacturer, currentPage, itemsOnPage, sortBySerialNumber, sortByConnectionDate, filterByManufacturer, filterByFirmwareVersion, filterByCompany, filterByCountry, filterByArchitecture, filterByModel, user, embossersNotifications, mode])

  useEffect(() => {
    localStorage.setItem("embossersDisplayMode", JSON.stringify(displayMode))
  }, [displayMode])

  return (
    <Embossers.Provider
      value={{
        embossers,
        embossersCount,
        search,
        availableManufacturers,
        availableFirmwareVersions,
        availableDistributors,
        availableCountryCodes,
        availableArchitectures,
        availableModels,
        pagesCount,
        currentPage,
        sortBySerialNumber,
        sortByConnectionDate,
        filterByManufacturer,
        filterByFirmwareVersion,
        filterByCompany,
        filterByCountry,
        filterByArchitecture,
        filterByModel,
        resultsListLength,
        itemsOnPage,
        displayMode,
        setSearch,
        setCurrentPage,
        setDisplayMode,
        setItemsOnPage,
        filtersList,
        setSortBySerialNumber,
        setSortByConnectionDate,
        setFilterByManufacturer,
        setFilterByFirmwareVersion,
        setFilterByCompany,
        setFilterByCountry,
        setFilterByArchitecture,
        setFilterByModel,
        error
      }}
    >
      {children}
    </Embossers.Provider>
  )
}

export const useEmbossers = () => useContext(Embossers)
